import { useQuery } from "react-query";
import { useSupabase } from "./use-supbase";
import { useSession } from "@supabase/auth-helpers-react";
import { Logger } from "#/lib/logging";
import { UseQueryResult } from "react-query/types/react/types";
import { assert } from "#/utils/general";
import { useRealtimeSubscription } from "#next/hooks/with-react-context/use-realtime-subscription/use-realtime-subscription";
import { useStripeProducts } from "#next/hooks/use-stripe-products";
import { supabaseThen } from "#/lib/supabase/supabase-then";
import { UTCDate } from "@date-fns/utc";
import { addMonths } from "date-fns/addMonths";
import { differenceInMonths } from "date-fns/differenceInMonths";

export interface Subscription {
  id: string | null,
  status: string,
  productId: string | null,
  createdAt: string,
  tier: "free" | "pro" | "enterprise",
  willCancelOn: string | null
}

// Default free tier subscription
const DEFAULT_FREE_SUBSCRIPTION: Subscription = {
  id: null,
  status: "free",
  productId: null,
  createdAt: "",
  tier: "free",
  willCancelOn: null
};

type SubscriptionResult =
  & Pick<UseQueryResult<Subscription | null, Error>, "data" | "error" | "isLoading" | "isFetching" | "refetch" | "isFetched" | "isFetchedAfterMount">
  & {
    tier: "free" | "pro" | "enterprise"
  };

export function useSubscription(): SubscriptionResult {
  const supabase = useSupabase();
  const session = useSession();
  const {
    data: products,
    isLoading: isLoadingProducts
  } = useStripeProducts();
  const userId = session?.user?.id;
  // Use react-query to fetch and manage subscription data
  const result = useQuery<Subscription | null, Error>([ "subscription", userId, products ], async () => {
    assert(!!userId, "Session user ID must be defined");
    assert(!!products, "Products must be defined");
    try {
      const { data, error: supabaseError } = await supabase.from("user_subscriptions")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "active")
        .limit(1)
        .single()
        .then(supabaseThen(new Error().stack));

      if (supabaseError) {
        // If no subscription is found, default to free tier
        if (supabaseError.code === "PGRST116") {
          return DEFAULT_FREE_SUBSCRIPTION;
        }
        throw supabaseError;
      }

      const product = products.find(p => p.id === data.product_id);
      if (!product) {
        return DEFAULT_FREE_SUBSCRIPTION;
      }

      let willCancelOn: string | null = null;
      if (data.cancel_at_period_end) {
        const now = new UTCDate();
        const createdAt = new UTCDate(data.created_at);
        const numMonthsElapsed = differenceInMonths(now, createdAt);
        willCancelOn = addMonths(createdAt, numMonthsElapsed + 1).toISOString();
      }

      if (data.status === "active") {
        return {
          id: data.id,
          status: data.status,
          productId: data.product_id,
          createdAt: new UTCDate(data.created_at).toISOString(),
          tier: product.tier,
          willCancelOn: willCancelOn
        } satisfies Subscription;
      }

      return DEFAULT_FREE_SUBSCRIPTION;
    } catch (err: any) {
      Logger.error({
        error: err,
        message: "Failed to fetch subscription",
        metadata: { userId: userId }
      });
      throw err;
    }
  }, {
    enabled: !!userId && !isLoadingProducts && !!products,
    retry: false
  });

  useRealtimeSubscription({
    table: "user_subscriptions",
    onInsert: async data => {
      if (userId && data.new.user_id === userId) {
        await result.refetch();
      }
    },
    onUpdate: async data => {
      if (userId && data.new.user_id === userId) {
        await result.refetch();
      }
    },
    onDelete: async data => {
      const resultDataId = result.data?.id;
      if (!!resultDataId && data.old.id === resultDataId) {
        await result.refetch();
      }
    }
  });

  return {
    data: result.data ?? DEFAULT_FREE_SUBSCRIPTION,
    isLoading: result.isLoading,
    isFetched: result.isFetched,
    isFetching: result.isFetching,
    isFetchedAfterMount: result.isFetchedAfterMount,
    error: result.error,
    refetch: result.refetch,
    tier: result.data?.tier ?? "free"
  };
}
